import { Icon } from '@iconify/react/dist/iconify.js';
import { useState } from 'react';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { storePendingPolicyCreation } from '../../api/equipmentApi';
import { createTicket, fetchContactsByAccount, createContact } from '../../api/ticketData';

// Reusable InputField component
const InputField = ({ label, value, onChange, fieldKey, placeholder = "Enter value...", isTextarea = false }) => {
    return (
        <div className="space-y-2">
            <label className="text-xs text-font-secondary font-semibold">{label}</label>
            {isTextarea ? (
                <textarea
                    value={value || ''}
                    onChange={(e) => onChange(fieldKey, e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                />
            ) : (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(fieldKey, e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
            )}
        </div>
    );
};

const NewPolicy = ({ onBack }) => {
    const [policyData, setPolicyData] = useState({
        policy_type: '',
        policy_text: ''
    });
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    const handlePolicyChange = (fieldKey, value) => {
        setPolicyData(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleReview = () => {
        setIsReviewMode(true);
    };

    const handleBackToEdit = () => {
        setIsReviewMode(false);
    };

    const handleExitAttempt = () => {
        if (Object.keys(policyData).filter(key => policyData[key]).length > 0) {
            setShowExitConfirm(true);
        } else {
            onBack();
        }
    };

    const handleConfirmExit = () => {
        setShowExitConfirm(false);
        onBack();
    };

    const handleCancelExit = () => {
        setShowExitConfirm(false);
    };

    if (isReviewMode) {
        return (
            <NewReviewPolicy
                onBack={handleBackToEdit}
                onSuccess={onBack}
                policyData={policyData}
            />
        );
    }

    return (
        <div className="responsive-page-layout scrollable">
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-center text-2xl">
                    <Icon icon="proicons:box-add" className="text-primary text-3xl"></Icon>
                    Add New Policy
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={handleExitAttempt}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Back</span>
                    </button>
                    
                    {Object.keys(policyData).filter(key => policyData[key]).length > 0 && (
                        <button 
                            onClick={handleReview}
                            className="flex flex-row gap-2 items-center text-font-primary bg-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary/80 transition-colors duration-300"
                        >
                            <Icon icon="mdi:eye" className="text-xl text-primary group-hover:text-primary" />
                            <span className="text-sm font-medium text-font-primary group-hover:text-primary">Review</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-col items-start gap-3 w-full">
                <div className="flex flex-row items-center gap-1">
                    <Icon icon="material-symbols:policy-rounded" className="text-2xl text-accent" />
                    <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Policy Details</label>
                </div>
                <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                    <InputField
                        label="Policy Type"
                        value={policyData.policy_type}
                        onChange={handlePolicyChange}
                        fieldKey="policy_type"
                        placeholder="Enter policy type..."
                    />
                    <hr className="w-full border-t-2 border-bgAccent" />
                    <InputField
                        label="Policy Text"
                        value={policyData.policy_text}
                        onChange={handlePolicyChange}
                        fieldKey="policy_text"
                        placeholder="Enter policy text..."
                        isTextarea={true}
                    />
                </div>
            </div>

            {/* Exit Confirmation Modal */}
            {showExitConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-font-primary mb-4">Unsaved Changes</h3>
                        <p className="text-sm text-font-secondary mb-6">
                            You have unsaved changes. Are you sure you want to exit?
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleCancelExit}
                                className="px-4 py-2 bg-bgSecondary text-font-primary rounded hover:bg-accent transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmExit}
                                className="px-4 py-2 bg-primary text-background rounded hover:bg-primary/80 transition-colors"
                            >
                                Exit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// New Review Policy Component
const NewReviewPolicy = ({ onBack, onSuccess, policyData }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const user = useAuthUser();

    // Get or create contact in the specified account (Wards)
    const getOrCreateContact = async (email, accountId) => {
        try {
            // First, try to find existing contact in this account
            const contacts = await fetchContactsByAccount(accountId);
            const existingContact = contacts.find(
                (c) => c.email === email && (c.accountId === accountId || c.account?.id === accountId)
            );

            if (existingContact) {
                console.log('NewReviewPolicy: Found existing contact:', existingContact.id || existingContact.contactId);
                return existingContact.id || existingContact.contactId;
            }

            // Contact doesn't exist - create it in the specified account
            console.log('NewReviewPolicy: Creating new contact in account:', accountId);
            const emailParts = email.split('@')[0].split('.');
            const firstName = emailParts[0] || 'Contact';
            const lastName = emailParts[1] || 'User';

            const newContact = await createContact({
                email: email,
                accountId: accountId,
                firstName: firstName,
                lastName: lastName
            });

            console.log('NewReviewPolicy: Created new contact:', newContact.id || newContact.contactId);
            return newContact.id || newContact.contactId;
        } catch (error) {
            console.error('NewReviewPolicy: Error getting/creating contact:', error);
            throw new Error(`Failed to get or create contact: ${error.message}`);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            const customerId = 'wards';
            
            if (Object.keys(policyData).filter(key => policyData[key]).length > 0) {
                await storePendingPolicyCreation(customerId, policyData);
                console.log('✅ Policy creation request stored in Cosmos DB successfully');

                // Step 2: Create Zoho Desk ticket (always create a ticket for tracking)
                console.log('🔄 Creating Zoho Desk ticket...');
                
                try {
                    // Wards account ID
                    const wardsAccountId = '1094595000000560001';
                    
                    // Get or create contact in Wards account for mohit.bansal@helios-core.com
                    const assigneeEmail = 'mohit.bansal@helios-core.com';
                    console.log('NewReviewPolicy: Getting/creating contact for:', assigneeEmail, 'in account:', wardsAccountId);
                    
                    const contactId = await getOrCreateContact(assigneeEmail, wardsAccountId);
                    console.log('NewReviewPolicy: Contact ID obtained:', contactId);

                    // Build subject
                    const subject = `New Policy: ${policyData.policy_type || 'New Policy'}`;

                    // Build description
                    let description = `Policy: New Policy<br>`;
                    description += '<br>-------<br><br>';

                    // Add policy details
                    if (Object.keys(policyData).filter(key => policyData[key]).length > 0) {
                        description += 'Policy Details:<br><br>';
                        Object.keys(policyData).filter(key => policyData[key]).forEach(fieldKey => {
                            const fieldLabel = fieldKey === 'policy_type' ? 'Policy Type' : 
                                             fieldKey === 'policy_text' ? 'Policy Text' : 
                                             fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                            const value = policyData[fieldKey];
                            
                            description += `${fieldLabel} - ${value}<br><br>`;
                        });
                        description += '----<br><br>';
                    }

                    // Create ticket data
                    const ticketData = {
                        subject: subject,
                        description: description,
                        email: assigneeEmail,
                        contactId: contactId,
                        departmentId: '1094595000000006907',
                        classification: 'Problem',
                        priority: 'High',
                        channel: 'Web',
                        language: 'en',
                        accountId: wardsAccountId,
                    };

                    console.log('NewReviewPolicy: Creating ticket with data:', {
                        accountId: ticketData.accountId,
                        contactId: ticketData.contactId,
                        email: ticketData.email,
                        subject: ticketData.subject
                    });

                    await createTicket(ticketData);
                    console.log('✅ Zoho Desk ticket created successfully');
                } catch (ticketError) {
                    console.error('❌ NewReviewPolicy: Failed to create ticket:', ticketError);
                    console.error('❌ NewReviewPolicy: Error details:', {
                        message: ticketError.message,
                        response: ticketError.response?.data,
                        stack: ticketError.stack
                    });
                    // Don't throw - allow the submission to succeed even if ticket creation fails
                    console.warn('⚠️ NewReviewPolicy: Ticket creation failed, but changes were saved to Cosmos DB');
                }

                setSubmitSuccess(true);
                
                setTimeout(() => {
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to submit policy:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            // Parse error response - handle FastAPI/Pydantic validation errors
            let errorMessage = 'Failed to submit policy';
            const errorData = error.response?.data;
            
            if (errorData) {
                // Handle validation error array (FastAPI format)
                if (Array.isArray(errorData.detail)) {
                    errorMessage = errorData.detail.map(err => {
                        if (typeof err === 'object' && err.msg) {
                            const loc = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : '';
                            return loc ? `${loc}: ${err.msg}` : err.msg;
                        }
                        return typeof err === 'string' ? err : JSON.stringify(err);
                    }).join('; ');
                }
                // Handle single validation error object
                else if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.msg) {
                    const loc = Array.isArray(errorData.detail.loc) ? errorData.detail.loc.slice(1).join('.') : '';
                    errorMessage = loc ? `${loc}: ${errorData.detail.msg}` : errorData.detail.msg;
                }
                // Handle string detail
                else if (typeof errorData.detail === 'string') {
                    errorMessage = errorData.detail;
                }
                // Handle message field
                else if (errorData.message) {
                    errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
                }
            }
            
            // Fallback to error message if nothing else worked
            if (errorMessage === 'Failed to submit policy' && error.message) {
                errorMessage = error.message;
            }
            
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="responsive-page-layout scrollable">
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-center text-2xl">
                    <Icon icon="mdi:eye" className="text-primary text-3xl"></Icon>
                    Review New Policy
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={onBack}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Back to Edit</span>
                    </button>
                    
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex flex-row gap-2 items-center text-font-primary bg-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary/80 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Icon 
                            icon="material-symbols:rate-review-rounded" 
                            className="text-xl text-primary group-hover:text-primary" 
                        />
                        <span className="text-sm font-medium text-font-primary group-hover:text-primary">
                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                        </span>
                    </button>
                </div>
            </div>

            {submitSuccess && (
                <div className="w-full bg-green-900 text-green-300 border border-green-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:check-circle-outline" className="text-xl" />
                        <span className="text-sm">Policy submitted successfully!</span>
                    </div>
                </div>
            )}

            {submitError && (
                <div className="w-full bg-red-900 text-red-300 border border-red-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:error-outline" className="text-xl" />
                        <span className="text-sm">{submitError}</span>
                    </div>
                </div>
            )}

            <div className="flex flex-col items-start gap-3 w-full">
                <div className="flex flex-row items-center gap-1">
                    <Icon icon="material-symbols:policy-rounded" className="text-2xl text-accent" />
                    <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Policy Details</label>
                </div>
                <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                    <div className="space-y-2">
                        <label className="text-xs text-font-secondary font-medium">Policy Type</label>
                        <p className="text-sm text-font-primary">{policyData.policy_type || 'N/A'}</p>
                    </div>
                    <hr className="w-full border-t-2 border-bgAccent" />
                    <div className="space-y-2">
                        <label className="text-xs text-font-secondary font-medium">Policy Text</label>
                        <p className="text-sm text-font-primary whitespace-pre-wrap">{policyData.policy_text || 'N/A'}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewPolicy;

